import PropTypes from 'prop-types'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components/macro'

import { formatPrice } from 'utilities/format'
import {
  getPriceEvaluationPointerPosition,
  normalizePriceEvaluation,
  priceEvaluationColors,
  priceEvaluationScaleColors,
  priceEvaluationSteps,
} from 'utilities/price-evaluation'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizings.lvl1};
  padding: ${({ theme }) => theme.sizeByFactor(1.25)}
    ${({ theme }) => theme.sizings.lvl2};
  background-color: ${({ theme }) => theme.colors.sectionBackground};
  font-family: ${({ theme }) => theme.font};
  color: ${({ theme }) => theme.colors.text};
`

const PointerRow = styled.div`
  position: relative;
  height: ${({ theme }) => theme.sizeByFactor(4.5)};
`

const Pointer = styled.div`
  position: absolute;
  top: 0;
  left: ${({ $position }) => $position}%;
  transform: translateX(-50%);
  padding: ${({ theme }) => theme.sizeByFactor(0.75)};
  background-color: ${({ theme, $color }) => theme.colors[$color]};
  color: ${({ theme }) => theme.colors.textOnDarkBackground};
  font-size: 11px;
  font-weight: 600;
  line-height: ${({ theme }) => theme.smallFontSize};
  text-align: center;
  white-space: nowrap;
`

const Bar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizeByFactor(0.375)};
`

const Segment = styled.div`
  flex: 1 1 0;
  height: ${({ theme }) => theme.sizings.lvl1};
  background-color: ${({ theme, $color }) => theme.colors[$color]};
`

const Labels = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizeByFactor(1.25)};
`

const Category = styled.div`
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.sizeByFactor(0.5)};
  min-width: 0;
`

const CategoryName = styled.span`
  color: ${({ theme, $color }) => theme.colors[$color]};
  font-size: 11px;
  font-weight: 600;
  line-height: ${({ theme }) => theme.smallFontSize};
  white-space: nowrap;
`

const Range = styled.span`
  font-size: 11px;
  font-weight: 400;
  line-height: ${({ theme }) => theme.smallFontSize};
  white-space: nowrap;
`

const formatAmount = (value) => formatPrice(value, false, false, true)

// Keeps the pointer inside the scale: it is about 15% of the scale wide
const clampPosition = (position) => Math.min(Math.max(position, 8), 92)

/**
 * Price scale of the evaluation modal: the five categories with their price
 * ranges and a pointer showing where the price of this car sits.
 */
const PriceEvaluationScale = ({
  price,
  evaluation,
  ranges,
  className,
  ...restProps
}) => {
  const { t } = useTranslation()
  const step = normalizePriceEvaluation(evaluation)
  const position = getPriceEvaluationPointerPosition(price, ranges)

  return (
    <Container className={className} {...restProps}>
      {position !== null && (
        <PointerRow>
          <Pointer
            $position={clampPosition(position)}
            $color={priceEvaluationColors[step] || 'priceEvaluationNoneText'}
          >
            {t('priceEvaluationDialog.thisVehicle')}
            <br />
            {`€${formatAmount(price)}`}
          </Pointer>
        </PointerRow>
      )}
      <Bar>
        {priceEvaluationSteps.map((category) => (
          <Segment
            key={category}
            $color={priceEvaluationScaleColors[category]}
          />
        ))}
      </Bar>
      <Labels>
        {priceEvaluationSteps.map((category, index) => (
          <Category key={category}>
            <CategoryName $color={priceEvaluationColors[category]}>
              {t(`priceEvaluation.${category}`)}
            </CategoryName>
            <Range>
              {`€ ${formatAmount(ranges[index].min)} - ${formatAmount(
                ranges[index].max,
              )}`}
            </Range>
          </Category>
        ))}
      </Labels>
    </Container>
  )
}

const rangeShape = PropTypes.shape({
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
})

PriceEvaluationScale.propTypes = {
  /** Price of this car, used to position the pointer on the scale */
  price: PropTypes.number,
  /** A PriceEvaluationEnum value or the 1-based step number (1 = very good price) */
  evaluation: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** One price range per category, from very good to high */
  ranges: PropTypes.arrayOf(rangeShape).isRequired,
  className: PropTypes.string,
}

PriceEvaluationScale.defaultProps = {
  price: null,
  evaluation: null,
  className: null,
}

export default PriceEvaluationScale
